import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Save, Settings2, Share2, Star, Trash2, Upload } from "lucide-react";
import { uploadFile } from "@/lib/upload";

const SOCIALS = [
  { key: "social_instagram", label: "Instagram", placeholder: "https://instagram.com/immortalvedas", testid: "set-social-instagram" },
  { key: "social_linkedin", label: "LinkedIn", placeholder: "https://linkedin.com/company/immortalvedas", testid: "set-social-linkedin" },
  { key: "social_facebook", label: "Facebook", placeholder: "https://facebook.com/immortalvedas", testid: "set-social-facebook" },
  { key: "social_twitter", label: "Twitter / X", placeholder: "https://x.com/immortalvedas", testid: "set-social-twitter" },
  { key: "social_threads", label: "Threads", placeholder: "https://threads.net/@immortalvedas", testid: "set-social-threads" },
];

export default function AdminSettings() {
  const [s, setS] = useState({
    whatsapp_number: "",
    gst_rate: 0.05,
    clinic_name: "Immortal Vedas",
    clinic_address: "",
    clinic_gstin: "",
    logo_url: "",
    site_url: "",
  });
  const [busy, setBusy] = useState(false);
  const [socialBusy, setSocialBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);

  const saveReviewRequests = async (patch) => {
    setReviewBusy(true);
    try {
      const { data } = await api.put("/settings", patch);
      setS(data);
      toast.success("Review request settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setReviewBusy(false); }
  };

  const sweepReviewRequests = async () => {
    setReviewBusy(true);
    try {
      const { data } = await api.post("/admin/orders/sweep-review-requests");
      toast.success(
        data.skipped
          ? "Review requests are switched off"
          : `${data.sent || 0} of ${data.considered || 0} due buyer(s) emailed`
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setReviewBusy(false); }
  };

  const saveSocial = async () => {
    setSocialBusy(true);
    try {
      const { data } = await api.put("/settings", Object.fromEntries(
        SOCIALS.map(({ key }) => [key, (s[key] || "").trim()]),
      ));
      setS(data);
      toast.success("Social links saved — the footer is already showing them");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSocialBusy(false); }
  };

  const clearSocial = async (key, label) => {
    try {
      const { data } = await api.put("/settings", { [key]: "" });
      setS(data);
      toast.success(`${label} removed — its icon is gone from the footer`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    api.get("/settings").then(({ data }) => setS(data)).catch(() => {});
  }, []);

  const onLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLogoBusy(true);
    try {
      // Logos keep their original format so transparent PNGs stay transparent.
      const { url } = await uploadFile(f, "branding", { raw: true });
      const { data } = await api.put("/settings", { logo_url: url });
      setS(data);
      toast.success("Logo updated — it now shows in the navbar");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLogoBusy(false); }
  };

  const removeLogo = async () => {
    try {
      const { data } = await api.put("/settings", { logo_url: "" });
      setS(data);
      toast.success("Logo removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", {
        ...s,
        gst_rate: parseFloat(s.gst_rate) || 0,
      });
      setS(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Card className="border-border">
      <CardContent className="p-6 lg:p-8">
        <h3 className="font-display text-2xl mb-1 flex items-center gap-2"><Settings2 size={18}/> Practice Settings</h3>
        <p className="text-sm text-muted-foreground mb-6">Configure your clinic details, GST rate and WhatsApp order number. These reflect across the app.</p>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 rounded-xl border border-border/70 bg-muted/30 p-4">
            <Label className="overline">Brand Logo</Label>
            <p className="text-[11px] text-muted-foreground mt-1 mb-3">
              Shown in the navbar in place of the leaf mark. A square PNG with a transparent background works best.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <span className="w-14 h-14 rounded-full bg-primary/10 border border-border flex items-center justify-center overflow-hidden shrink-0">
                {s.logo_url
                  ? <img src={mediaUrl(s.logo_url)} alt="Brand logo" className="w-full h-full object-contain" data-testid="set-logo-preview"/>
                  : <span className="text-[10px] text-muted-foreground text-center px-1">No logo</span>}
              </span>
              <div>
                <Input type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogo} className="max-w-xs" data-testid="set-logo-upload"/>
                {logoBusy && <div className="text-xs text-muted-foreground mt-1.5" data-testid="set-logo-uploading">Uploading…</div>}
              </div>
              {s.logo_url && (
                <Button variant="outline" size="sm" onClick={removeLogo} className="btn-pill" data-testid="set-logo-remove">
                  Remove
                </Button>
              )}
              {!s.logo_url && <Upload size={16} className="text-muted-foreground"/>}
            </div>
          </div>
          <div>
            <Label className="overline">Clinic Name</Label>
            <Input value={s.clinic_name} onChange={(e)=>setS({...s,clinic_name:e.target.value})} className="mt-2 bg-transparent" data-testid="set-clinic-name"/>
          </div>
          <div>
            <Label className="overline">WhatsApp Number (incl. country code)</Label>
            <Input value={s.whatsapp_number} onChange={(e)=>setS({...s,whatsapp_number:e.target.value})}
              placeholder="+91 98xxxxxxxx" className="mt-2 bg-transparent" data-testid="set-whatsapp"/>
          </div>
          <div className="sm:col-span-2">
            <Label className="overline">Clinic Address</Label>
            <Textarea rows={2} value={s.clinic_address || ""} onChange={(e)=>setS({...s,clinic_address:e.target.value})} className="mt-2 bg-transparent" data-testid="set-address"/>
          </div>
          <div className="sm:col-span-2">
            <Label className="overline">Website Address (used in emails &amp; reminders)</Label>
            <Input value={s.site_url || ""} onChange={(e)=>setS({...s,site_url:e.target.value})}
              placeholder="https://www.immortalvedas.com" className="mt-2 bg-transparent" data-testid="set-site-url"/>
          </div>
          <div>
            <Label className="overline">GSTIN</Label>
            <Input value={s.clinic_gstin || ""} onChange={(e)=>setS({...s,clinic_gstin:e.target.value})}
              placeholder="15-digit GSTIN" className="mt-2 bg-transparent" data-testid="set-gstin"/>
          </div>
          <div>
            <Label className="overline">GST Rate (decimal, e.g. 0.05 for 5%)</Label>
            <Input type="number" step="0.01" min="0" max="1" value={s.gst_rate}
              onChange={(e)=>setS({...s,gst_rate:e.target.value})}
              className="mt-2 bg-transparent" data-testid="set-gst-rate"/>
            <p className="text-[11px] text-muted-foreground mt-1">Ayurvedic classical medicines are typically 5% GST in India.</p>
          </div>
        </div>

        <Button onClick={save} disabled={busy} className="btn-pill bg-primary text-primary-foreground mt-6" data-testid="set-save">
          <Save size={14} className="mr-1.5"/> {busy ? "Saving…" : "Save Settings"}
        </Button>

        <div className="border-t border-border mt-10 pt-8" data-testid="set-social-section">
          <div className="flex items-center gap-2 mb-1">
            <Share2 size={16} className="text-secondary" />
            <h3 className="font-display text-xl">Social media</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-5">
            Paste the full address of each profile. Clear a field and save to hide that icon from the
            footer straight away — no redeploy needed. WhatsApp uses the number above.
          </p>
          <div className="grid sm:grid-cols-2 gap-5">
            {SOCIALS.map(({ key, label, placeholder, testid }) => (
              <div key={key}>
                <Label className="overline">{label}</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={s[key] || ""}
                    onChange={(e) => setS({ ...s, [key]: e.target.value })}
                    placeholder={placeholder}
                    className="bg-transparent"
                    data-testid={testid}
                  />
                  {s[key] && (
                    <Button
                      variant="outline"
                      onClick={() => clearSocial(key, label)}
                      className="btn-pill border-border shrink-0 px-3"
                      title={`Remove the ${label} link`}
                      data-testid={`${testid}-clear`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <Button onClick={saveSocial} disabled={socialBusy} className="btn-pill bg-primary text-primary-foreground mt-6" data-testid="set-social-save">
            <Save size={14} className="mr-1.5"/> {socialBusy ? "Saving…" : "Save social links"}
          </Button>
        </div>

        <div className="border-t border-border mt-10 pt-8" data-testid="set-review-section">
          <div className="flex items-center gap-2 mb-1">
            <Star size={16} className="text-secondary" />
            <h3 className="font-display text-xl">Review requests</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-5">
            A few days after an order is marked delivered, the buyer gets one email asking them to
            rate what they bought, with a direct link to each product. Sent once per order.
          </p>
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-3 rounded-xl border border-border px-4 py-3">
              <span className="text-sm">Send review requests</span>
              <Switch
                checked={s.review_request_enabled !== false}
                onCheckedChange={(v) => saveReviewRequests({ review_request_enabled: v })}
                data-testid="set-review-enabled"
              />
            </div>
            <div>
              <Label className="overline">Days after delivery</Label>
              <Input
                type="number"
                min="1"
                max="30"
                value={s.review_request_days ?? 3}
                onChange={(e) => setS({ ...s, review_request_days: e.target.value })}
                onBlur={(e) => saveReviewRequests({ review_request_days: parseFloat(e.target.value) || 3 })}
                className="mt-2 bg-transparent w-28"
                data-testid="set-review-days"
              />
            </div>
            <Button
              variant="outline"
              onClick={sweepReviewRequests}
              disabled={reviewBusy}
              className="btn-pill border-border mt-5"
              data-testid="set-review-sweep"
            >
              {reviewBusy ? "Working…" : "Send due requests now"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
